import React from "react";
import { Download, Landmark, FileText, Eye, RefreshCw } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { BANK_DETAILS, PAYMENT_METHODS, DOCS } from "@/mock/data";
import { SelectField, FileField } from "@/components/fields/Field";
import { openFileById } from "@/lib/api";

// ===================== STEP 10 : Application Fee =====================
export function Step10({ errors = {} }) {
  const { application, setField } = useApp();
  const b = BANK_DETAILS;
  React.useEffect(() => {
    if (!application.data.fee_method) setField("fee_method", "UPI");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="animate-fade-up">
      {/* UPI QR box */}
      <div className="mb-5 flex flex-col items-center rounded-lg border border-emerald-200 bg-white p-5 text-center">
        <div className="mb-1 flex items-center gap-2 text-[15px] font-bold text-[#3a6b1c]">
          <Landmark className="h-5 w-5" /> अर्ज शुल्क ₹{b.amount}/- — UPI ने भरा
        </div>
        <p className="mb-3 text-[13px] text-slate-500">कोणतेही UPI अ‍ॅप (GPay, PhonePe, Paytm, BHIM) उघडून खालील QR स्कॅन करा</p>
        <img src="/upi-qr-500.png" alt="UPI QR ₹500" data-testid="upi-qr-image" className="h-52 w-52 rounded-lg border border-slate-200 p-2" />
        <div className="mt-3 text-[13.5px]">
          <span className="text-slate-500">UPI ID: </span>
          <span className="font-latin font-bold text-slate-800" data-testid="upi-id">{b.upiId}</span>
        </div>
        <div className="text-[13.5px]"><span className="text-slate-500">रक्कम: </span><span className="font-bold text-[#16a34a]">₹{b.amount}/-</span></div>
        <div className="text-[13px] text-slate-600">देय: {b.accountName}</div>
      </div>

      {/* Bank details box */}
      <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-5">
        <div className="mb-4 flex items-center gap-2 text-[15px] font-bold text-[#3a6b1c]">
          <Landmark className="h-5 w-5" /> बँक ट्रान्सफरसाठी तपशील
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Info label="बँकेचे नाव" value={b.bankName} />
          <Info label="खातेदाराचे नाव" value={b.accountName} />
          <Info label="खाते क्रमांक" value={b.accountNo} />
          <Info label="IFSC कोड" value={b.ifsc} />
          <Info label="शाखेचे नाव" value={b.branch} />
        </div>
        <div className="mt-4">
          <p className="text-[13px] text-slate-500">पत्ता</p>
          <p className="text-[14px] font-semibold text-slate-800">{b.address}</p>
        </div>
      </div>

      <div className="mb-4 rounded-lg border-l-4 border-[#E0A106] bg-[#fdf7e5] p-4 text-[13.5px] leading-relaxed text-[#5c4a1c]">
        <strong>सूचना:</strong> वरील QR स्कॅन करून किंवा बँक तपशीलावर अर्ज शुल्क ₹{b.amount}/- भरा आणि त्याचा UTR क्रमांक व पेमेंट पुरावा येथे अपलोड करा.
      </div>

      <div className="rounded-lg border border-slate-200 p-5">
        <p className="mb-4 text-[15px] font-bold text-slate-800">अर्ज शुल्क भरण्याची माहिती <span className="text-[#E0A106]">*</span></p>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <SelectField label="पेमेंटची पद्धत" name="fee_method" required options={PAYMENT_METHODS} errors={errors} />
          <div>
            <label className="mb-1.5 block text-[14px] font-semibold text-slate-700">UTR क्रमांक <span className="text-[#E0A106]">*</span></label>
            <input
              value={application.data.fee_utr ?? ""}
              onChange={(e) => setField("fee_utr", e.target.value)}
              className={`w-full rounded-md border px-3 py-2 text-[14px] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/15 ${errors.fee_utr ? "border-[#dc2626]" : "border-slate-300"}`}
            />
            {errors.fee_utr && <p className="mt-1 text-[12.5px] text-[#dc2626]">{errors.fee_utr}</p>}
          </div>
          <FileField label="पेमेंट अदा केलेला स्क्रीनशॉट/पुरावा अपलोड करा" name="fee_proof" required compact errors={errors} />
        </div>
        <div className="mt-4 rounded-lg border-l-4 border-[#dc2626] bg-red-50 p-3.5 text-[13px] leading-relaxed text-[#7f1d1d]" data-testid="payment-fraud-caution">
          <strong>सावधान:</strong> अयशस्वी (failed) पेमेंट किंवा पेमेंटबाबत खोटी/फसवी माहिती दिल्याचे आढळल्यास आपला अर्ज अवैध ठरविला जाईल.
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[13px] text-slate-500">{label}</p>
      <p className="text-[15px] font-bold text-slate-800 font-latin">{value}</p>
    </div>
  );
}

// ===================== STEP 11 : Documents =====================
const FILE_LABELS = {
  photo: "अर्जदाराचा फोटो",
  aadhaar_file: "आधारकार्ड",
  pan_file: "पॅनकार्ड",
  name_change_file: "नाव बदल गॅझेट / विवाह दाखला",
  residence_proof_file: "रहिवासी पत्त्याचा पुरावा",
  self_declaration_file: "स्वघोषणापत्र",
  csc_file: "CSC अनुभव पुरावा",
  bk_file: "बँकिंग प्रतिनिधी पुरावा",
  eg_file: "ई-गव्हर्नन्स अनुभव पुरावा",
  pc_file: "महा-ई सेवा केंद्र अनुभव पुरावा",
  cv_file: "चारित्र्य पडताळणी पुरावा",
  cv_affidavit_file: "प्रतिज्ञापत्र",
  fee_proof: "अर्ज शुल्क पेमेंट पुरावा",
  bp_pdf: "व्यवसाय आराखडा दस्तऐवज",
};
export function fileLabel(key) {
  if (FILE_LABELS[key]) return FILE_LABELS[key];
  const m = key.match(/^(edu|cl|infra|fin|doc)_file_(\d+)$/);
  if (m) {
    const map = { edu: "शैक्षणिक प्रमाणपत्र", cl: "संगणक साक्षरता प्रमाणपत्र", infra: "पायाभूत सुविधा पुरावा", fin: "आर्थिक क्षमता पुरावा", doc: "कागदपत्र" };
    return `${map[m[1]]} #${Number(m[2]) + 1}`;
  }
  const s = key.match(/^(si_[a-z]+)_file$/);
  if (s) return "सामाजिक समावेशकता पुरावा";
  return key;
}

function fileStep(key) {
  if (["photo", "aadhaar_file", "pan_file", "name_change_file", "residence_proof_file", "self_declaration_file"].includes(key)) return 1;
  if (/^edu_file_\d+$/.test(key)) return 2;
  if (/^cl_file_\d+$/.test(key)) return 3;
  if (/^infra_file_\d+$/.test(key)) return 4;
  if (/^fin_file_\d+$/.test(key)) return 5;
  if (["csc_file", "bk_file", "eg_file", "pc_file"].includes(key)) return 6;
  if (key === "bp_pdf") return 7;
  if (/^si_[a-z]+_file$/.test(key)) return 8;
  if (["cv_file", "cv_affidavit_file"].includes(key)) return 9;
  if (key === "fee_proof") return 10;
  return 99;
}

function DocRow({ srNo, fieldName, file }) {
  const { uploadFile } = useApp();
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const view = () => openFileById(file.fileId);
  const onReplace = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > 1024 * 1024) { e.target.value = ""; return; }
    setBusy(true);
    try { await uploadFile(fieldName, f); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <tr className="border-t border-slate-200 align-top" data-testid={`doc-row-${fieldName}`}>
      <td className="p-2.5 text-center font-semibold text-slate-600">{srNo}</td>
      <td className="p-2.5 text-center">
        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#1b5e3f]/10 px-2 text-[12px] font-bold text-[#1b5e3f]">{fileStep(fieldName)}</span>
      </td>
      <td className="p-2.5 font-semibold text-[#1b3d2a]">{fileLabel(fieldName)}</td>
      <td className="p-2.5">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="max-w-[160px] truncate text-[12.5px] text-slate-500">{file.name}</span>
        </div>
        <div className="mt-1.5 flex gap-2">
          <button type="button" onClick={view} data-testid={`doc-view-${fieldName}`}
            className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-[12px] font-semibold text-slate-600 hover:border-[#1b5e3f] hover:text-[#1b5e3f]"><Eye className="h-3.5 w-3.5" /> पहा</button>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`doc-replace-${fieldName}`}
            className="flex items-center gap-1 rounded-full bg-[#E0A106] px-3 py-1 text-[12px] font-semibold text-[#1b3d2a] hover:bg-[#c88f05] disabled:opacity-60"><RefreshCw className="h-3.5 w-3.5" /> {busy ? "..." : "बदला"}</button>
          <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={onReplace} className="hidden" />
        </div>
      </td>
    </tr>
  );
}

export function Step11() {
  const { application } = useApp();
  const d = application.data || {};
  const files = Object.keys(d)
    .filter((k) => d[k] && typeof d[k] === "object" && d[k].fileId)
    .map((k) => ({ key: k, file: d[k] }))
    .sort((a, b) => fileStep(a.key) - fileStep(b.key));
  return (
    <div className="animate-fade-up">
      <p className="mb-4 text-[13.5px] text-slate-600">
        खाली मागील टप्प्यांमध्ये (१ ते १०) अपलोड केलेली सर्व कागदपत्रे दिसत आहेत. प्रत्येक कागदपत्राचे <strong>पहा</strong> बटणाने पूर्वावलोकन करा; आवश्यक असल्यास <strong>बदला</strong> बटणाने नवीन प्रत अपलोड करा.
      </p>
      {files.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-[14px] text-slate-400">
          अद्याप कोणतीही कागदपत्रे अपलोड केलेली नाहीत. कृपया मागील टप्प्यांमध्ये आवश्यक कागदपत्रे अपलोड करा.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr className="bg-[#1b5e3f] text-left text-white">
                <th className="p-2.5 text-center font-semibold">अ.क्र.</th>
                <th className="p-2.5 text-center font-semibold">स्टेप क्र.</th>
                <th className="p-2.5 font-semibold">जोडलेल्या पुराव्याचे नाव / तपशील</th>
                <th className="p-2.5 font-semibold">अपलोड फाईल</th>
              </tr>
            </thead>
            <tbody>
              {files.map(({ key, file }, i) => <DocRow key={key} srNo={i + 1} fieldName={key} file={file} />)}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-4 text-[12.5px] text-slate-500">एकूण अपलोड केलेली कागदपत्रे: <strong>{files.length}</strong></p>
    </div>
  );
}
